import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import moment from "moment";
import { Clock, Search, ChevronRight, Users, AlertCircle } from "lucide-react";
import { setDoctorDelay, getDoctors, getAppointments } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { ACTIVE_STATUSES } from "../lib/appointment-filters";
import { cn } from "../lib/utils";

const QUICK_MINUTES = [10, 15, 20, 30, 45, 60];
const ISO = "YYYY-MM-DD";

/** "09:00 AM" -> 540. Mirrors backend utils/time.js so both agree on order. */
function slotToMinutes(time) {
  const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec((time || "").trim());
  if (!m) return 0;
  let hours = parseInt(m[1], 10) % 12;
  if (/pm/i.test(m[3])) hours += 12;
  return hours * 60 + parseInt(m[2], 10);
}

const minutesToLabel = (mins) => {
  const wrapped = ((mins % 1440) + 1440) % 1440;
  return moment().startOf("day").add(wrapped, "minutes").format("hh:mm A");
};

/**
 * Groups a doctor's booked slots for the day into contiguous blocks, so the
 * desk picks a session ("09:00 AM – 10:20 AM · 4 patients") rather than
 * scanning individual slots. A gap longer than `gapMinutes` starts a new block.
 */
function buildSessions(appointments, gapMinutes = 60) {
  const sorted = [...appointments]
    .map((a) => ({ ...a, minutes: slotToMinutes(a.slot_time) }))
    .sort((a, b) => a.minutes - b.minutes);

  const sessions = [];
  for (const appt of sorted) {
    const current = sessions[sessions.length - 1];
    if (current && appt.minutes - current.endMinutes <= gapMinutes) {
      current.endMinutes = appt.minutes;
      current.appointments.push(appt);
    } else {
      sessions.push({
        startMinutes: appt.minutes,
        endMinutes: appt.minutes,
        appointments: [appt],
      });
    }
  }

  // The block ends when its last appointment does, not when it starts.
  return sessions.map((s) => ({
    ...s,
    key: `${s.startMinutes}-${s.endMinutes}`,
    label: `${minutesToLabel(s.startMinutes)} – ${minutesToLabel(s.endMinutes + 20)}`,
  }));
}

/**
 * Mark Delay.
 *
 * Opens on provider selection, or jumps straight to the schedule when a
 * `doctor` is supplied (the Doctors page already knows who). The delay itself
 * is per-doctor on the backend; picking a session is what tells the desk which
 * patients are affected before they commit.
 */
export default function MarkDelayModal({ open, onOpenChange, doctor: presetDoctor, onSaved }) {
  const [doctor, setDoctor] = useState(presetDoctor || null);
  const [doctors, setDoctors] = useState([]);
  const [query, setQuery] = useState("");

  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [session, setSession] = useState(null);

  const [minutes, setMinutes] = useState(20);
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  const today = moment().format(ISO);

  // Reset to a clean state each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setDoctor(presetDoctor || null);
    setQuery("");
    setSessions([]);
    setSession(null);
    setMinutes(20);
    setReason("");
    setNotify(true);
  }, [open, presetDoctor]);

  useEffect(() => {
    if (!open || presetDoctor) return;
    getDoctors({ limit: 200 })
      .then((res) => setDoctors(res.data || []))
      .catch(() => setDoctors([]));
  }, [open, presetDoctor]);

  // Once a provider is chosen, load today's active bookings for them.
  useEffect(() => {
    if (!open || !doctor?._id) return;
    setLoadingSessions(true);
    setSession(null);
    getAppointments({
      doctor_id: doctor._id,
      from: today,
      to: today,
      status: ACTIVE_STATUSES.join(","),
      limit: 100,
    })
      .then((res) => {
        const built = buildSessions(res.data || []);
        setSessions(built);
        if (built.length === 1) setSession(built[0]); // nothing to choose
      })
      .catch(() => setSessions([]))
      .finally(() => setLoadingSessions(false));
  }, [open, doctor?._id, today]);

  const filteredDoctors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return doctors;
    return doctors.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.specialty?.toLowerCase().includes(q) ||
        d.phone?.includes(q)
    );
  }, [doctors, query]);

  const affectedCount = session?.appointments.length ?? 0;
  const newStart = session ? minutesToLabel(session.startMinutes + Number(minutes || 0)) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!doctor) return;
    const value = Number(minutes);
    if (!Number.isInteger(value) || value < 1 || value > 480) {
      toast.error("Enter a delay between 1 and 480 minutes.");
      return;
    }

    setSaving(true);
    try {
      const res = await setDoctorDelay(doctor._id, { minutes: value, reason, notify });
      const notified = res.data.notified;
      toast.success(
        notified > 0
          ? `${doctor.name} delayed by ${value} min — ${notified} patient(s) notified.`
          : `${doctor.name} delayed by ${value} min.`
      );
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Could not mark delay.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Mark Delay
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ---- step 1: provider ---- */}
          <div className="space-y-1.5">
            <Label>Provider</Label>

            {doctor ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{doctor.name}</p>
                  {doctor.specialty && (
                    <p className="truncate text-xs text-muted-foreground">{doctor.specialty}</p>
                  )}
                </div>
                {!presetDoctor && (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto shrink-0 p-0 text-sm"
                    onClick={() => setDoctor(null)}
                  >
                    Change
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name, specialty, or mobile…"
                    className="pl-9"
                  />
                </div>

                <div className="max-h-64 overflow-y-auto rounded-md border border-border">
                  {filteredDoctors.length === 0 ? (
                    <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                      No providers found.
                    </p>
                  ) : (
                    filteredDoctors.map((d) => (
                      <button
                        key={d._id}
                        type="button"
                        onClick={() => setDoctor(d)}
                        className="flex w-full items-center justify-between gap-2 border-b border-border px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-muted/50"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="truncate text-sm font-medium">{d.name}</span>
                          <Badge variant="outline" className="shrink-0 font-normal">
                            {d.specialty || "Doctor"}
                          </Badge>
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {filteredDoctors.length} provider{filteredDoctors.length === 1 ? "" : "s"} found
                </p>
              </>
            )}
          </div>

          {/* ---- step 2: session ---- */}
          {doctor && (
            <div className="space-y-1.5">
              <Label>Today&rsquo;s schedule</Label>

              {loadingSessions ? (
                <div className="space-y-2">
                  <Skeleton className="h-11 w-full" />
                  <Skeleton className="h-11 w-full" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="flex items-start gap-2 rounded-md border border-dashed border-border bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    No active bookings today for {doctor.name}. You can still record the delay —
                    nobody will be notified.
                  </span>
                </div>
              ) : (
                <div className="overflow-hidden rounded-md border border-border">
                  {sessions.map((s) => {
                    const active = session?.key === s.key;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => setSession(s)}
                        className={cn(
                          "flex w-full items-center justify-between border-b border-border px-3 py-2.5 text-left transition-colors last:border-0",
                          active ? "bg-primary/10" : "hover:bg-muted/50"
                        )}
                      >
                        <span className="text-sm font-medium">{s.label}</span>
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Users className="h-3.5 w-3.5" />
                          {s.appointments.length}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ---- step 3: the delay itself ---- */}
          {doctor && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="delay-minutes">Delay (minutes)</Label>
                  <Input
                    id="delay-minutes"
                    type="number"
                    min={1}
                    max={480}
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>New start time</Label>
                  <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm">
                    {newStart || <span className="text-muted-foreground">Select a session</span>}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {QUICK_MINUTES.map((m) => (
                  <Button
                    key={m}
                    type="button"
                    size="sm"
                    variant={Number(minutes) === m ? "default" : "outline"}
                    className={cn("h-7 rounded-full px-3 text-xs", Number(minutes) !== m && "bg-card")}
                    onClick={() => setMinutes(m)}
                  >
                    {m}m
                  </Button>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="delay-reason">Reason</Label>
                <Input
                  id="delay-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Running late from another clinic"
                />
              </div>

              <label className="flex items-start gap-2 text-sm">
                <Checkbox checked={notify} onCheckedChange={setNotify} className="mt-0.5" />
                <span>
                  Notify affected patients via WhatsApp
                  {affectedCount > 0 && (
                    <span className="text-muted-foreground">
                      {" "}
                      — {affectedCount} in this session
                    </span>
                  )}
                </span>
              </label>
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!doctor || saving}>
              {saving ? "Saving…" : "Mark Delay"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
